import React, { createContext, useState, useEffect, useContext } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useNavigate, useLocation } from "react-router-dom"; // ✅ ADDED: to redirect new users to Choose Plan

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate(); // ✅ ADDED: used to route users after login
  const location = useLocation();

  const [currentUser, setCurrentUser] = useState(null);

  // ✅ ADDED: store Firestore user + settings in context (so ChoosePlan can read them)
  const [userData, setUserData] = useState(null);
  const [userSettings, setUserSettings] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true); // ✅ ADDED: ensure loading resets on auth changes
      setCurrentUser(user);

      // 🔴 Not logged in
      if (!user) {
        setUserData(null);       // ✅ ADDED: clear cached userData
        setUserSettings(null);   // ✅ ADDED: clear cached userSettings
        setLoading(false);
        return;
      }

      // ✅ 1) Ensure Firestore user doc exists
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          const email = user.email || "";
          const usernameFromEmail =
            email && email.includes("@")
              ? email.split("@")[0]
              : `kairos-${Math.random().toString(36).slice(2, 7)}`;

          const newUserData = {
            uid: user.uid,
            name: user.displayName || usernameFromEmail,
            email: email,
            phone: "",
            username: usernameFromEmail,
            bio: "Building with Kairos 🚀",
            provider: user.providerData?.[0]?.providerId || "unknown",
            authRef: user.uid,
            createdAt: serverTimestamp(),
            picture: user.photoURL || "",
            credits: 10,
          };

          await setDoc(userRef, newUserData);

          // ✅ ADDED: set context immediately for brand new users
          setUserData(newUserData);
        } else {
          // ✅ ADDED: store existing doc in context for app pages to use
          setUserData({ id: userSnap.id, ...userSnap.data() });
        }
      } catch (error) {
        console.error("❌ Error creating/checking user document:", error);
      }

      // ✅ 2) Ensure workspace doc exists
      try {
        const workspaceRef = doc(db, "workspaces", user.uid);
        const workspaceSnap = await getDoc(workspaceRef);

        if (!workspaceSnap.exists()) {
          const workspaceData = {
            userId: user.uid,
            name: "Websites",
            email: user.email || "",
            createdAt: serverTimestamp(),
          };

          await setDoc(workspaceRef, workspaceData);
        }
      } catch (error) {
        console.error("❌ Error creating/checking workspace document:", error);
      }

      // ✅ 3) Ensure default settings doc exists (subcollection)
      let latestSettings = null; // ✅ ADDED: capture latest settings for redirect decision

      try {
        const settingsRef = doc(db, "users", user.uid, "settings", "main");
        const settingsSnap = await getDoc(settingsRef);

        if (!settingsSnap.exists()) {
          const defaultSettings = {
            appearance: {
              theme: "system",
              reduceMotion: false,
              highContrast: false,
            },
            preferences: {
              language: "en",
              timezone: "Africa/Johannesburg",
            },
            editor: {
              autosave: true,
              autosaveIntervalSec: 10,
              showGrid: false,
              snapToGrid: true,
              aiSuggestions: true,
              aiAutoApplySafeEdits: false,
              defaultAiModel: "gpt-4.1",
            },
            notifications: {
              email: {
                productUpdates: true,
                tipsAndTutorials: true,
                securityAlerts: true,
                billing: true,
                publishStatus: true,
              },
              inApp: {
                productUpdates: true,
                securityAlerts: true,
                billing: true,
                publishStatus: true,
              },
            },
            privacy: {
              analytics: true,
              personalization: true,
              crashReports: true,
              privacyPolicy: true,
              termsAndConiditions: true,
              cookiePolicy: true,
              refundPolicy: true,
              acceptableUse: true,
              aiUsePolicy: true,
              subscriptionPolicy: true,
            },
            marketingConsents: {
              email: false,
              sms: false,
              whatsapp: false,
              partners: false,
            },
            marketingPrompt: true,
            receiveMarketing: false,

            // ✅ IMPORTANT: plan gate fields
            plan: "free",
            planStatus: "unselected", // ✅ New users should be forced to Choose Plan
            planKey: null,

            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          await setDoc(settingsRef, defaultSettings);

          // ✅ ADDED: store defaults as latestSettings for redirects and context
          latestSettings = defaultSettings;
          setUserSettings(defaultSettings);
        } else {
          // ✅ ADDED: store existing settings in context
          latestSettings = settingsSnap.data();
          setUserSettings({ id: settingsSnap.id, ...settingsSnap.data() });
        }

        // ✅ OPTION B: Backfill missing plan fields for older users
        const needsBackfill =
        latestSettings?.planStatus === undefined ||
        latestSettings?.planKey === undefined;

        if (needsBackfill) {
        await updateDoc(settingsRef, {
          plan: latestSettings?.plan ?? "free",
          planStatus: latestSettings?.planStatus ?? "unselected", // grandfather old users
          planKey: latestSettings?.planKey ?? (latestSettings?.plan ?? null),
          updatedAt: serverTimestamp(),
        });

        // Keep local state in sync
        latestSettings = {
          ...latestSettings,
          plan: latestSettings?.plan ?? "free",
          planStatus: latestSettings?.planStatus ?? "unselected",
          planKey: latestSettings?.planKey ?? (latestSettings?.plan ?? null),
        };

        setUserSettings((prev) => ({
          ...(prev || {}),
          ...latestSettings,
        }));
        }

      } catch (error) {
        console.error("❌ Error creating/checking settings document:", error);
      }

      // ✅ ADDED: redirect logic after userData/userSettings exist
      // Rule:
      // - If planStatus is not "active" -> go to /choose-plan
      // - Otherwise -> go to /workspaces
      // ✅ NEW: If user selected a plan before auth, go straight to checkout
      const pendingPlan = (localStorage.getItem("kairos_pending_plan") || "").trim();
      const onCallback = location.pathname === "/payment-callback";
      
      // ✅ Only use pendingPlan shortcut if:
      // - we are NOT on /payment-callback
      // - and user is not already in success/failed flow
      // - and we don't already have a resolved status from Firestore
      if (
        pendingPlan &&
        !onCallback &&
        latestSettings?.planStatus !== "success" &&
        latestSettings?.planStatus !== "failed"
      ) {
        try {
          const settingsRef = doc(db, "users", user.uid, "settings", "main");
      
          await updateDoc(settingsRef, {
            planKey: pendingPlan,        // ✅ keep planKey aligned
            plan: "paid",
            planStatus: "pending",
            updatedAt: serverTimestamp(),
          });
      
          localStorage.removeItem("kairos_pending_plan");
      
          navigate(`/checkout?plan=${encodeURIComponent(pendingPlan)}`, { replace: true });
          setLoading(false);
          return;
        } catch (err) {
          console.error("❌ Failed to set pending plan:", err);
        }
      }
    // ✅ Don't override navigation on these pages
    const ALLOW_ROUTES = [
      "/buy-credits",
      "/pricing",
      "/credits-purchase-outcome",
      "/kairos",
      "/websites",
      "/kairos",
    ];

    const isAllowedRoute = ALLOW_ROUTES.some((p) => location.pathname.startsWith(p));

    if (isAllowedRoute) {
      setLoading(false);
      return;
    }
    
    // ✅ Normal routing (no pending plan)
    const planStatus = latestSettings?.planStatus;
    const planKey = latestSettings?.planKey || "free";
    
    const upgradePlanStatusLocal = localStorage.getItem("upgrade_pending_plan");
    const upgradePlanStatus = upgradePlanStatusLocal;



    if(upgradePlanStatus) {
      localStorage.removeItem("upgrade_pending_plan")
    }

    

    if (planStatus === "unselected") {
      navigate("/choose-plan", { replace: true });
    }

    else if (planStatus === "success" || planStatus === "failed") {
      navigate("/payment-outcome", { replace: true });
    }
    
    else if (planStatus === "pending") {
      navigate(`/checkout?plan=${encodeURIComponent(planKey)}`, { replace: true });
    }
    
    else if (upgradePlanStatusLocal) {
      navigate(`/upgrade-checkout?plan=${encodeURIComponent(upgradePlanStatus)}`, { replace: true });
    }
    
    else {
      navigate("/workspace", { replace: true });
    }

      setLoading(false);
    });

    return unsubscribe;
  }, [navigate, location.pathname, location.search]); // ✅ ADDED: include navigate in deps

  return (
    <AuthContext.Provider value={{ currentUser, userData, userSettings, loading }}>
      {/* ✅ Existing behavior: only render children when auth is ready */}
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
